import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { apiAgentGetMemberBets, ApiRequestError } from '@/lib/api'
import type { AgentBetItem, PaginatedResult, RoundResult } from '@nexbit/types'
import { formatCurrency } from '@/lib/utils'
import { format } from 'date-fns'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT } from '@/contexts/language'

function resultLabel(betType: string, result: RoundResult | null): string | null {
  if (!result) return null
  const isHighLow = betType === 'high' || betType === 'low'
  return isHighLow ? result.highLow : result.oddEven
}

function betStatusVariant(status: string): 'success' | 'destructive' | 'warning' | 'outline' {
  if (status === 'settled' || status === 'won') return 'success'
  if (status === 'rolled_back') return 'destructive'
  if (status === 'pending') return 'warning'
  return 'outline'
}

export default function AgentReportsMemberPage() {
  const t = useT()
  const { memberId } = useParams<{ memberId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const from = searchParams.get('from') ?? ''
  const to = searchParams.get('to') ?? ''
  const username = searchParams.get('username') ?? memberId ?? ''

  const [data, setData] = useState<PaginatedResult<AgentBetItem> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)

  const load = useCallback(async (p: number) => {
    if (!memberId || !from || !to) { setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      setData(await apiAgentGetMemberBets(memberId, { from, to, page: p, limit: 50 }))
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setLoading(false)
    }
  }, [memberId, from, to, t])

  useEffect(() => { void load(page) }, [load, page])

  const totalPages = data ? Math.ceil(data.total / data.limit) : 0

  function handleBack() {
    const q = new URLSearchParams({ from, to })
    navigate(`/agent/reports?${q.toString()}`)
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl">
      <div className="flex items-center gap-3 mb-5">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 font-mono text-xs text-muted-foreground"
          onClick={handleBack}
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t.betReport.backToReport}
        </Button>
        <span className="text-muted-foreground/40">·</span>
        <span className="font-mono text-xs font-bold uppercase tracking-wider text-foreground">
          {username} — {t.betReport.memberBets}
        </span>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4" role="alert">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
        <div className="px-4 py-2.5 bg-secondary flex items-center justify-between">
          <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
            {t.betReport.memberBets}
          </span>
          {data && !loading && (
            <span className="font-mono text-xs text-secondary-foreground/60">{data.total} {t.betReport.betCount}</span>
          )}
        </div>

        {loading ? (
          <div className="divide-y divide-border">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex gap-4 px-4 py-3">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="py-12 text-center">
            <p className="font-mono text-sm text-muted-foreground">{t.betReport.noActivity}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.betReport.date}</TableHead>
                <TableHead>{t.betReport.game}</TableHead>
                <TableHead>{t.betReport.betType}</TableHead>
                <TableHead className="text-right">{t.betReport.betVolume}</TableHead>
                <TableHead className="text-right">{t.betReport.payout}</TableHead>
                <TableHead>{t.betReport.result}</TableHead>
                <TableHead>{t.common.status}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((b) => {
                const won = b.winAmount !== null && parseFloat(b.winAmount) > 0
                return (
                  <TableRow key={b.id}>
                    <TableCell>
                      <time dateTime={b.createdAt} className="font-mono text-xs text-muted-foreground">
                        {format(new Date(b.createdAt), 'dd/MM HH:mm')}
                      </time>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{b.gameCode}</TableCell>
                    <TableCell className="font-mono text-xs uppercase">
                      {b.betType}
                      {b.number && <span className="ml-1.5 font-semibold text-foreground">{b.number}</span>}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatCurrency(b.amount)}</TableCell>
                    <TableCell className={cn('text-right font-mono text-sm', won ? 'text-success font-semibold' : 'text-muted-foreground')}>
                      {b.winAmount ? formatCurrency(b.winAmount) : '—'}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const label = resultLabel(b.betType, b.result)
                        return label ? (
                          <Badge variant="outline" className="font-mono text-[10px] uppercase">{label}</Badge>
                        ) : '—'
                      })()}
                    </TableCell>
                    <TableCell>
                      <Badge variant={betStatusVariant(b.status)} className="font-mono text-[10px]">
                        {b.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}

        {totalPages > 1 && !loading && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/30">
            <span className="font-mono text-xs text-muted-foreground">{page} / {totalPages}</span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="ก่อน">
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} aria-label="ถัดไป">
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
